
import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { StartupAccount } from "@/types/startup";
import StartupDashboard from "@/components/startup/StartupDashboard";
import StartupProfileForm from "@/components/startup/StartupProfileForm";
import StartupApplicationForm from "@/components/startup/StartupApplicationForm";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Rocket, Users, Calendar, BookOpen, Newspaper, Briefcase, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const Teenvisor = () => {
  const { user } = useAuth();
  const [startupAccount, setStartupAccount] = useState<StartupAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [needsProfile, setNeedsProfile] = useState(false);

  useEffect(() => {
    if (user) {
      fetchStartupAccount();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchStartupAccount = async () => {
    try {
      const { data, error } = await supabase
        .from("startup_accounts")
        .select("*")
        .eq("user_id", user?.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error("Error fetching startup account:", error);
      } else if (data) {
        setStartupAccount(data);
      } else {
        // Check if user signed up as startup type
        const accountType = localStorage.getItem('accountType');
        if (accountType === 'startup') {
          setNeedsProfile(true);
          localStorage.removeItem('accountType');
        }
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileComplete = () => {
    setNeedsProfile(false);
    fetchStartupAccount();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>
              Please sign in to access Teenvisor for Startups.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Show profile form for new startup users
  if (needsProfile) {
    return <StartupProfileForm onProfileComplete={handleProfileComplete} />;
  }

  if (startupAccount?.status === 'approved') {
    return <StartupDashboard />;
  }

  if (startupAccount?.status === 'pending') {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Rocket className="h-6 w-6 text-primary" />
              Application Under Review
            </CardTitle>
            <CardDescription>
              Your startup application is currently being reviewed by our team.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-medium text-yellow-800">Application Details</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  <strong>Startup Name:</strong> {startupAccount.startup_name}
                </p>
                <p className="text-sm text-yellow-700">
                  <strong>Submitted:</strong> {new Date(startupAccount.submitted_at).toLocaleDateString()}
                </p>
              </div>
              <p className="text-muted-foreground">
                We'll notify you once your application has been processed. This typically takes 2-3 business days.
              </p>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Having issues or questions? Please contact our support team for assistance.
                </AlertDescription>
              </Alert>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (startupAccount?.status === 'rejected') {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="text-red-600">Application Rejected</CardTitle>
            <CardDescription>
              Unfortunately, your startup application was not approved at this time.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-muted-foreground">
                You can reapply after addressing the feedback provided or try again in 30 days.
              </p>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Need clarification on the rejection? Please contact our support team for detailed feedback.
                </AlertDescription>
              </Alert>
              <Button onClick={() => setShowApplicationForm(true)}>
                Apply Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showApplicationForm) {
    return (
      <StartupApplicationForm 
        onSuccess={() => {
          fetchStartupAccount();
          setShowApplicationForm(false);
        }}
        onCancel={() => setShowApplicationForm(false)}
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-2">
            <Rocket className="h-8 w-8 text-primary" />
            Teenvisor for Startups
          </h1>
          <p className="text-xl text-muted-foreground mb-6">
            Connect with teen communities and build meaningful relationships with the next generation
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader>
              <Users className="h-8 w-8 text-primary mb-2" />
              <CardTitle className="text-lg">Community Building</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Create and manage startup communities to engage with teen users
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Calendar className="h-8 w-8 text-primary mb-2" />
              <CardTitle className="text-lg">Event Management</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Host workshops, webinars, and networking events for teens
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Briefcase className="h-8 w-8 text-primary mb-2" />
              <CardTitle className="text-lg">Opportunities</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Post internships, tasks, and collaboration opportunities
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <BookOpen className="h-8 w-8 text-primary mb-2" />
              <CardTitle className="text-lg">Course Creation</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Build and share educational content with teen learners
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Newspaper className="h-8 w-8 text-primary mb-2" />
              <CardTitle className="text-lg">News Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Share your startup stories and insights with the community
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="text-center">
          <CardHeader>
            <CardTitle>Ready to Get Started?</CardTitle>
            <CardDescription>
              Apply to join Teenvisor for Startups and start building meaningful connections with teen users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button size="lg" onClick={() => setShowApplicationForm(true)}>
              Apply Now
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Teenvisor;
